import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_CONFIG } from "../../../../utils/apiConfig";
import B2BLoadingSpinner from "../../B2BShared/B2BLoadingSpinner/B2BLoadingSpinner";

/**
 * Auto-login component.
 * Accessed via /gcb/auto-login?token=<jwt>
 * Validates the token against the backend, stores credentials, and redirects to dashboard.
 */
const B2BAutoLogin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const token = searchParams.get("token");

    if (!token) {
      navigate("/gcb/login", { replace: true });
      return;
    }

    // Validate the token by calling /api/b2b/auth/me with it
    const autoLogin = async () => {
      try {
        const response = await fetch(`${API_CONFIG.B2B_API_URL}/api/b2b/auth/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Invalid token");
        }

        const data = await response.json();

        if (data.success && data.data?.user) {
          // Store credentials — user is now logged in
          localStorage.setItem("b2bToken", token);
          localStorage.setItem("b2bUser", JSON.stringify(data.data.user));

          // Navigate to dashboard (full page reload to pick up the new auth state)
          window.location.href = "/gcb/dashboard";
        } else {
          throw new Error("Invalid response");
        }
      } catch {
        // Token invalid or expired — send to login
        navigate("/gcb/login", { replace: true });
      }
    };

    autoLogin();
  }, [searchParams, navigate]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <B2BLoadingSpinner />
    </div>
  );
};

export default B2BAutoLogin;
